/**
 * #2712 CLIENTERROR: the browser's half of "errors people hit reach the server".
 *
 * `reportClientError(kind, error, extra?)` posts one report to
 * POST /api/client-error, which writes it to the Worker log. Three callers:
 * the window `error` and `unhandledrejection` listeners that
 * `installClientErrorReporting()` adds once at startup (client-error-boot.ts,
 * imported first by main.tsx), and ChunkLoadErrorBoundary's componentDidCatch.
 *
 * Per page load (this module's lifetime; SPA navigation does not reset it):
 *   - one report per distinct (page, message, top stack frame), so a render
 *     loop or a polling error sends once;
 *   - at most MAX_REPORTS_PER_LOAD reports in all, an urgent one apart;
 *   - sent one at a time, each send starting SEND_GAP_MS after the last one
 *     started, because the server's per-session window is one KV key and KV
 *     takes about one write per key per second. An `urgent` report (the
 *     boundary's chunk-load catch, which reloads the page) goes to the front,
 *     past the cap, and when it repeats a report still queued, that report
 *     moves to the front instead and the caller waits on it. The boundary
 *     reloads once flushClientErrorReports() says the queue has left, or at
 *     its deadline (review: a chunk report past the cap, or a duplicate of a
 *     queued one, was lost to the reload).
 *
 * It never throws, and it never reports its own failure: a send that rejects
 * or throws is swallowed where it happens, and a report raised while another
 * is being built is dropped, so a failing POST cannot loop.
 *
 * WHAT A MESSAGE MAY SAY. Free text can carry a person's name, and neither a
 * denylist (review round 2) nor the error's class (round 3: a TypeError's
 * "(reading 'Jo Bloggs')", an app's own `new TypeError(...)`) can prove it
 * does not. So a message travels only as one of SAFE_MESSAGES
 * (src/shared/client-error-messages.ts, which the server holds every report
 * to): fixed engine, browser, React and chunk-load wording whose every
 * filled-in part (a property name, a variable, a value, a pattern) is cut to
 * "…". The output holds the template's own words and nothing captured from
 * the error, whatever its class.
 *   - An ApiError (lib/api-error.ts) reports its status and, when the body
 *     names one, its machine code ("API 503 (ostendo_unavailable)").
 *   - Anything else is "<Name>: [message withheld]". Its frames still name the
 *     throw site, and with the build's source map that is a file and line.
 *   - <Name> is the error's name when it is one of KNOWN_ERROR_NAMES (the
 *     engines', DOMException's and the app's own classes), else
 *     "[error name withheld]".
 * The stack is frames in the app's bundle only: the V8 head, every line of
 * the message in it, is cut here before src/shared/client-error.ts keeps the
 * frames on this origin's /assets/ files.
 *
 * Not reported: a 403 that is a real role decision (isAccessDenial) and a 401
 * (isSignedOut). Those are the server's answer about access, already in its
 * logs, not a fault in the page (the negative control in #2712). An RBAC
 * store outage wears a 403 too and IS reported, as everywhere else.
 *
 * Inert in test runtimes, like telemetry-events.ts (#1928): the default
 * transport sends nothing there. Tests build their own reporter with
 * createClientErrorReporter and a fake `send`.
 */
import { BUILD_ID } from "../../shared/build-id";
import { normaliseClientErrorReport, type ClientErrorBoundary, type ClientErrorKind } from "../../shared/client-error";
import { RBAC_STORE_OUTAGE_CODE, WITHHELD, apiSummary, reportErrorName, safeMessage } from "../../shared/client-error-messages";
import { devAuthHeaders } from "./api";
import { isAccessDenial, isRbacStoreOutage, isSignedOut } from "./api-error";

export type { ClientErrorKind } from "../../shared/client-error";
export { WITHHELD } from "../../shared/client-error-messages";

const ENDPOINT = "/api/client-error";

export const MAX_REPORTS_PER_LOAD = 5;
export const SEND_GAP_MS = 1_100;

export interface ClientErrorExtra {
  /** The error boundary's component name, for kind "boundary": one of CLIENT_ERROR_BOUNDARIES. */
  boundary?: ClientErrorBoundary;
  /** Send ahead of anything queued: the page is about to unload. */
  urgent?: boolean;
}

/** The two listeners need only this much of `window`. */
export interface ErrorEventTargetLike {
  addEventListener(type: "error" | "unhandledrejection", listener: (event: unknown) => void): void;
  removeEventListener(type: "error" | "unhandledrejection", listener: (event: unknown) => void): void;
}

export interface ClientErrorReporterDeps {
  /** Hand one JSON body to the network. A rejection or a throw is swallowed. */
  send: (body: string) => unknown;
  /** The current page's pathname. */
  pathname: () => string;
  /** The page's origin: a stack keeps only frames in the app's bundle on it. */
  origin: () => string;
  build: string;
  /** The pause between send starts. Tests pass one they control. */
  wait?: (ms: number) => Promise<void>;
}

export interface ClientErrorReporter {
  /**
   * Queue one report. The promise settles once that report has been handed to
   * `send`, or at once when it is not sent (filtered, a duplicate, past the
   * cap). Never rejects.
   */
  report(kind: ClientErrorKind, error: unknown, extra?: ClientErrorExtra): Promise<void>;
  /** Add the error and unhandledrejection listeners to `target`; returns the removal. */
  install(target: ErrorEventTargetLike): () => void;
  /** Settles once the queue is empty and the last gap has passed. Never rejects. */
  drain(): Promise<void>;
  /** Settles once every queued report has been handed to `send` (at once when none is queued). Never rejects. */
  flush(): Promise<void>;
}

/** A message the browser wrote on an error event that carried no error object. */
interface BrowserMessage {
  browserMessage: string;
}

/**
 * "API 409", or "API 503 (ostendo_unavailable)" when the body's JSON names an
 * `error_code` or `code` (and "rbac_store_outage" for that 403), the code
 * withheld when the app does not define it (apiSummary); null when `thrown`
 * is not an ApiError.
 */
function apiErrorSummary(thrown: object): string | null {
  const { status, body } = thrown as { status?: unknown; body?: unknown };
  if (typeof status !== "number" || typeof body !== "string") return null;
  let code: string | undefined;
  try {
    const parsed = JSON.parse(body) as { error_code?: unknown; code?: unknown } | null;
    const candidate = parsed?.error_code ?? parsed?.code;
    if (typeof candidate === "string" && candidate) code = candidate;
  } catch {
    // Not JSON: the status alone.
  }
  // The RBAC store's fail-closed 403 says so in a sentence, not a code.
  if (code === undefined && isRbacStoreOutage(thrown)) code = RBAC_STORE_OUTAGE_CODE;
  return apiSummary(status, code);
}

/**
 * V8 heads a stack with String(error), every line of a multi-line message
 * included, before the frames: "<name>: <message>", or one of them alone when
 * the other is empty. Rebuild that head from the error's own name and message
 * and cut it, so no line of it (a response body's "    at ..." line) can pass
 * as a frame. The head must be the stack's start and end at a line break:
 * JavaScriptCore and SpiderMonkey stacks begin with a frame and are left whole.
 * (Review: two heuristics for finding the head were each bypassed, by an
 * empty name and by a name with a colon; an exact rebuild has no rule to bypass.)
 */
function cutStackHead(stack: string, error: { name?: unknown; message?: unknown }): string {
  const name = error.name === undefined ? "Error" : String(error.name);
  const message = error.message === undefined ? "" : String(error.message);
  const head = name && message ? `${name}: ${message}` : name || message;
  if (!head || !stack.startsWith(head)) return stack;
  const rest = stack.slice(head.length);
  return rest === "" || rest.startsWith("\n") ? rest : stack;
}

/** The message a report may carry, and the stack with its head cut (frames are filtered by the normaliser). */
function describeThrown(thrown: unknown): { message: string; stack?: string } {
  if (thrown && typeof thrown === "object" && typeof (thrown as BrowserMessage).browserMessage === "string") {
    // "Uncaught TypeError: ..." on an error event that lost its error object.
    const raw = (thrown as BrowserMessage).browserMessage.replace(/^Uncaught (?:\(in promise\) )?/, "");
    const named = /^([A-Za-z][A-Za-z0-9]{0,62}): ([^]*)$/.exec(raw);
    const safe = safeMessage(named ? named[2] : raw);
    if (!safe) return { message: `Browser message: ${WITHHELD}` };
    const name = named ? reportErrorName(named[1]) : "Error";
    return { message: name === "Error" ? safe : `${name}: ${safe}` };
  }
  if (thrown && typeof thrown === "object" && typeof (thrown as { message?: unknown }).message === "string") {
    const e = thrown as { name?: unknown; message: string; stack?: unknown };
    const stack = typeof e.stack === "string" ? cutStackHead(e.stack, e) : undefined;
    const api = apiErrorSummary(thrown);
    if (api) return { message: api, stack };
    const name = reportErrorName(e.name);
    const safe = safeMessage(e.message);
    if (safe) return { message: name === "Error" ? safe : `${name}: ${safe}`, stack };
    return { message: e.message ? `${name}: ${WITHHELD}` : name, stack };
  }
  if (typeof thrown === "string") return { message: `Non-Error string: ${WITHHELD}` };
  if (thrown === null || thrown === undefined) return { message: `Non-Error ${String(thrown)}` };
  return { message: `Non-Error ${typeof thrown}: ${WITHHELD}` };
}

export function createClientErrorReporter(deps: ClientErrorReporterDeps): ClientErrorReporter {
  const wait = deps.wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const seen = new Set<string>();
  type Queued = { key: string; body: string; urgent: boolean; handedOff: () => void; sent: Promise<void> };
  const queue: Queued[] = [];
  /** The reports still in `queue`, by dedupe key, so an urgent duplicate can find its original. */
  const waiting = new Map<string, Queued>();
  const flushed: Array<() => void> = [];
  let queued = 0;
  let building = false;
  let idle: Promise<void> = Promise.resolve();
  let pumping = false;

  async function pump(): Promise<void> {
    try {
      while (queue.length) {
        const next = queue.shift()!;
        waiting.delete(next.key);
        try {
          // Not awaited: the gap runs from this send's start, so a slow reply
          // never pushes the next report (an urgent one included) further out.
          void Promise.resolve(deps.send(next.body)).then(
            () => undefined,
            () => undefined, // Never reported: a failing POST must not become the next report.
          );
        } catch {
          // Never reported, as above.
        }
        next.handedOff();
        if (!queue.length) for (const settle of flushed.splice(0)) settle();
        try {
          await wait(SEND_GAP_MS);
        } catch {
          // A broken timer only shortens the gap.
        }
      }
    } finally {
      // The same microtask as the loop's last empty-queue check, with no await
      // between: a report queued after that check always starts a new pump.
      pumping = false;
    }
  }

  /** An urgent report goes after any urgent one already waiting, ahead of the rest. */
  function place(item: Queued): void {
    const firstOrdinary = item.urgent ? queue.findIndex((q) => !q.urgent) : -1;
    if (firstOrdinary === -1) queue.push(item);
    else queue.splice(firstOrdinary, 0, item);
  }

  function enqueue(key: string, body: string, urgent: boolean): Promise<void> {
    let handedOff!: () => void;
    const sent = new Promise<void>((resolve) => {
      handedOff = resolve;
    });
    const item: Queued = { key, body, urgent, handedOff, sent };
    place(item);
    waiting.set(key, item);
    if (!pumping) {
      pumping = true;
      idle = pump().catch(() => undefined);
    }
    return sent;
  }

  function report(kind: ClientErrorKind, error: unknown, extra?: ClientErrorExtra): Promise<void> {
    const urgent = extra?.urgent === true;
    if (building || (!urgent && queued >= MAX_REPORTS_PER_LOAD)) return Promise.resolve();
    building = true;
    try {
      if (isAccessDenial(error) || isSignedOut(error)) return Promise.resolve();
      const { message, stack } = describeThrown(error);
      const normalised = normaliseClientErrorReport(
        {
          kind,
          page: deps.pathname(),
          message,
          stack,
          boundary: extra?.boundary,
          build: deps.build,
        },
        deps.origin(),
      );
      if (!normalised) return Promise.resolve();
      const key = `${normalised.page}\n${normalised.message}\n${normalised.stack?.split("\n", 1)[0] ?? ""}`;
      if (seen.has(key)) {
        // Sent already, or not urgent: nothing to wait for. Still queued and
        // urgent: the page is about to unload, so move it to the front and
        // wait on it, as if this report were it.
        const original = waiting.get(key);
        if (!original || !urgent) return Promise.resolve();
        if (!original.urgent) {
          queue.splice(queue.indexOf(original), 1);
          original.urgent = true;
          place(original);
        }
        return original.sent;
      }
      seen.add(key);
      queued += 1;
      return enqueue(key, JSON.stringify(normalised), urgent);
    } catch {
      // Reporting must never interrupt the application.
      return Promise.resolve();
    } finally {
      building = false;
    }
  }

  function install(target: ErrorEventTargetLike): () => void {
    const onError = (event: unknown) => {
      const e = event as { error?: unknown; message?: unknown } | null;
      if (e?.error !== undefined && e?.error !== null) void report("error", e.error);
      else if (typeof e?.message === "string") void report("error", { browserMessage: e.message } satisfies BrowserMessage);
      else void report("error", undefined);
    };
    const onRejection = (event: unknown) => {
      void report("unhandledrejection", (event as { reason?: unknown } | null)?.reason);
    };
    target.addEventListener("error", onError);
    target.addEventListener("unhandledrejection", onRejection);
    return () => {
      target.removeEventListener("error", onError);
      target.removeEventListener("unhandledrejection", onRejection);
    };
  }

  function flush(): Promise<void> {
    if (!queue.length) return Promise.resolve();
    return new Promise<void>((resolve) => flushed.push(resolve));
  }

  return { report, install, drain: () => idle, flush };
}

const IS_TEST_RUNTIME = typeof import.meta !== "undefined" && import.meta.env?.MODE === "test";

function endpointUrl(): URL | null {
  try {
    const url = new URL(ENDPOINT, window.location.origin);
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

/** The real transport: a same-origin keepalive POST, so a started report survives the reload a chunk error triggers. */
function browserSend(body: string): Promise<unknown> | undefined {
  if (IS_TEST_RUNTIME || typeof window === "undefined") return undefined;
  const url = endpointUrl();
  if (!url) return undefined;
  return fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", ...devAuthHeaders() },
    body,
    keepalive: true,
  });
}

function browserPathname(): string {
  return typeof window === "undefined" ? "/" : window.location.pathname;
}

function browserOrigin(): string {
  return typeof window === "undefined" ? "" : window.location.origin;
}

const browserReporter = (): ClientErrorReporter =>
  createClientErrorReporter({ send: browserSend, pathname: browserPathname, origin: browserOrigin, build: BUILD_ID });

let active: ClientErrorReporter = browserReporter();
let uninstall: (() => void) | null = null;

/**
 * Report one error. Never throws. The promise settles once the report has
 * been handed to the network (or at once when it is not sent), and never
 * rejects: a caller about to unload the page can wait on it.
 */
export function reportClientError(kind: ClientErrorKind, error: unknown, extra?: ClientErrorExtra): Promise<void> {
  try {
    return active.report(kind, error, extra);
  } catch {
    return Promise.resolve();
  }
}

/**
 * Settles once every report queued so far has been handed to the network, at
 * once when none is. Never rejects. The chunk-error reload waits on it (with
 * its own deadline, so a dead network still reloads).
 */
export function flushClientErrorReports(): Promise<void> {
  try {
    return active.flush();
  } catch {
    return Promise.resolve();
  }
}

/** Add the window listeners, once. A second call is a no-op. */
export function installClientErrorReporting(): void {
  if (uninstall || typeof window === "undefined") return;
  try {
    uninstall = active.install(window as unknown as ErrorEventTargetLike);
  } catch {
    uninstall = null;
  }
}

/** Tests only: route reportClientError to `reporter` (null restores the browser one). */
export function setClientErrorReporterForTests(reporter: ClientErrorReporter | null): void {
  uninstall?.();
  uninstall = null;
  active = reporter ?? browserReporter();
}
